import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, Loader2, ArrowRight, Save } from "lucide-react";
import { AppShell } from "@/components/app/AppShell";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { MonthPicker } from "@/components/MonthPicker";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { ListEditor } from "@/components/ListEditor";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { METRIC_DEFS } from "@/lib/metrics";

const EXAMPLE = "Paste anything: board notes, Slack recap, a rough paragraph. e.g. 'June: MRR crossed 82k (was 71k), cash 1.2m, burning ~140k so ~8mo runway. Team 14. 320 customers, churn 3.2%. Closed Acme. Onboarding still clunky. Need Series A intros.'";

function currentMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export default function StructureNotes() {
  const { company } = useAuth();
  const navigate = useNavigate();
  const [raw, setRaw] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);
  const [period, setPeriod] = useState(currentMonth());

  const extract = async () => {
    if (raw.trim().length < 5) return toast.error("Please paste some notes first");
    setLoading(true);
    try {
      const { data } = await api.post(`/ai/structure`, { raw_text: raw });
      setResult(data);
      toast.success("AI structured your notes. Review & edit below.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "AI structuring failed");
    } finally { setLoading(false); }
  };

  const setMetric = (k, v) => setResult((r) => ({ ...r, metrics: { ...r.metrics, [k]: v === "" ? null : Number(v) } }));
  const setList = (k, v) => setResult((r) => ({ ...r, [k]: v }));

  const saveMetrics = async () => {
    if (!period) return toast.error("Choose a period to save metrics");
    setSaving(true);
    try {
      const payload = { period };
      METRIC_DEFS.forEach((d) => { payload[d.key] = result.metrics?.[d.key] ?? null; });
      await api.post(`/companies/${company.id}/metrics`, payload);
      toast.success("Metrics saved to " + period);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save metrics");
    } finally { setSaving(false); }
  };

  const useInUpdate = () => {
    navigate("/app/updates/new", { state: { prefill: { period, structured: result } } });
  };

  return (
    <AppShell title="AI Structure Notes" subtitle="Turn messy notes into clean metrics & talking points">
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card className="rounded-xl border bg-card p-5">
          <Label htmlFor="raw" className="text-sm font-semibold">Your raw notes</Label>
          <Textarea id="raw" value={raw} onChange={(e) => setRaw(e.target.value)} placeholder={EXAMPLE} className="mt-2 min-h-[280px]" data-testid="ai-raw-notes-textarea" />
          <Button onClick={extract} disabled={loading} className="mt-4 gap-2" data-testid="ai-extract-button">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Structure with AI
          </Button>
        </Card>

        <Card className="rounded-xl border bg-card p-5" data-testid="ai-structured-panel">
          <div className="mb-3 flex items-center justify-between">
            <span className="text-sm font-semibold">Extracted (editable)</span>
            {result && <Badge data-testid="structure-ai-draft" className="border-brand/30 bg-brand/10 text-brand-ink">AI draft</Badge>}
          </div>
          {!result ? (
            <div className="flex h-[280px] flex-col items-center justify-center text-center text-sm text-muted-foreground">
              <Sparkles className="mb-2 h-6 w-6 text-primary/40" />
              Structured metrics and talking points will appear here.
            </div>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                {METRIC_DEFS.map((d) => (
                  <div key={d.key}>
                    <Label className="text-xs">{d.label}</Label>
                    <Input type="number" step="any" value={result.metrics?.[d.key] ?? ""} onChange={(e) => setMetric(d.key, e.target.value)} className="mt-1 h-9" data-testid={`struct-metric-${d.key}`} />
                  </div>
                ))}
              </div>
              <ListEditor label="Highlights" items={result.highlights} onChange={(v) => setList("highlights", v)} testId="struct-highlights" />
              <ListEditor label="Lowlights" items={result.lowlights} onChange={(v) => setList("lowlights", v)} testId="struct-lowlights" />
              <ListEditor label="Asks" items={result.asks} onChange={(v) => setList("asks", v)} testId="struct-asks" />

              <div className="flex flex-wrap items-end gap-3 border-t pt-4">
                <div>
                  <Label htmlFor="sp" className="text-xs">Save to period</Label>
                  <MonthPicker value={period} onChange={setPeriod} className="mt-1 h-9 w-44" data-testid="struct-period" />
                </div>
                <Button variant="outline" onClick={saveMetrics} disabled={saving} className="gap-2" data-testid="struct-save-metrics">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save metrics
                </Button>
                <Button onClick={useInUpdate} className="gap-2" data-testid="struct-use-in-update">
                  Use in new update <ArrowRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </Card>
      </div>
    </AppShell>
  );
}
